/**
 * @fileoverview Validation utilities for Iranian national codes and other address extension fields.
 */

/**
 * Validates an Iranian National Code (کد ملی).
 * The 10-digit code has a checksum algorithm.
 *
 * @param code - The national code to validate (10 digits)
 * @returns true if valid, false otherwise
 */
export function validateIranianNationalCode(code: string): boolean {
  // Must be 10 digits
  if (!/^\d{10}$/.test(code)) {
    return false;
  }

  // Check for invalid patterns (all same digits)
  if (/^(\d)\1{9}$/.test(code)) {
    return false;
  }

  // Calculate checksum
  const digits = code.split("").map(Number);
  let sum = 0;

  for (let i = 0; i < 9; i++) {
    sum += digits[i] * (10 - i);
  }

  const remainder = sum % 11;
  const checkDigit = remainder < 2 ? remainder : 11 - remainder;

  return checkDigit === digits[9];
}

/**
 * Validates an Iranian Company National Code (شناسه ملی).
 * The 11-digit code has a checksum algorithm.
 *
 * @param code - The company national code to validate (11 digits)
 * @returns true if valid, false otherwise
 */
export function validateIranianCompanyNationalCode(code: string): boolean {
  // Must be 11 digits
  if (!/^\d{11}$/.test(code)) {
    return false;
  }

  // Iranian company national code (شناسه ملی حقوقی) validation
  // Based on persian-tools algorithm: http://www.aliarash.com/article/shenasameli/shenasa_meli.htm
  const controlDigit = parseInt(code.slice(10, 11));
  const d = parseInt(code.slice(9, 10)) + 2;
  const z = [29, 27, 23, 19, 17];

  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += (d + parseInt(code.slice(i, i + 1))) * z[i % 5];
  }

  sum = sum % 11;
  if (sum === 10) {
    sum = 0;
  }

  return controlDigit === sum;
}

/**
 * Validates an Iranian Economic Code (کد اقتصادی).
 * Must be exactly 12 digits.
 *
 * @param code - The economic code to validate (12 digits)
 * @returns true if valid, false otherwise
 */
export function validateIranianEconomicCode(code: string): boolean {
  // Must be 12 digits
  return /^\d{12}$/.test(code);
}

/**
 * Validates GPS latitude coordinate.
 *
 * @param lat - Latitude value
 * @returns true if valid (-90 to 90), false otherwise
 */
export function validateLatitude(lat: number | null | undefined): boolean {
  if (lat === null || lat === undefined) {
    return false;
  }
  return lat >= -90 && lat <= 90;
}

/**
 * Validates GPS longitude coordinate.
 *
 * @param lng - Longitude value
 * @returns true if valid (-180 to 180), false otherwise
 */
export function validateLongitude(lng: number | null | undefined): boolean {
  if (lng === null || lng === undefined) {
    return false;
  }
  return lng >= -180 && lng <= 180;
}

/**
 * File validation configuration.
 */
export const FILE_VALIDATION_CONFIG = {
  maxSize: 5 * 1024 * 1024, // 5MB
  allowedMimeTypes: ["image/jpeg", "image/png", "application/pdf"],
  allowedExtensions: [".jpg", ".jpeg", ".png", ".pdf"],
};

/**
 * Validates a file for upload.
 *
 * @param file - The file to validate
 * @returns Validation result with error message if invalid
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  // Check file size
  if (file.size > FILE_VALIDATION_CONFIG.maxSize) {
    return {
      valid: false,
      error: `file_too_large`, // Translation key
    };
  }

  // Check MIME type
  if (!FILE_VALIDATION_CONFIG.allowedMimeTypes.includes(file.type)) {
    return {
      valid: false,
      error: `invalid_file_type`, // Translation key
    };
  }

  // Check extension
  const extension = "." + file.name.split(".").pop()?.toLowerCase();
  if (!FILE_VALIDATION_CONFIG.allowedExtensions.includes(extension)) {
    return {
      valid: false,
      error: `invalid_file_extension`, // Translation key
    };
  }

  return { valid: true };
}

/**
 * Formats a file size in bytes to a human-readable string.
 *
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "2.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
